import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Mail, Lock, Eye, EyeOff, ArrowLeft, XCircle } from 'lucide-react';
import '../styles/landing.css';

const Login = () => {
    const navigate = useNavigate();
    const { login } = useAuth();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    const [formData, setFormData] = useState({
        email: '',
        password: '',
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!formData.email || !formData.password) {
            setError('Please fill in all fields');
            return;
        }

        setLoading(true);

        const result = await login(formData.email, formData.password);

        setLoading(false);

        if (result.success) {
            // Developer users go to /dev dashboard
            if (result.user.is_developer) {
                navigate('/dev');
            }
            // Check if employee is in preboarding lifecycle stage
            else if (!result.user.is_admin && result.user.lifecycle_stage === 'preboarding') {
                // Redirect to preboarding dashboard with token
                if (result.user.preboarding_token) {
                    navigate(`/preboard/${result.user.preboarding_token}/dashboard`);
                } else {
                    setError('Preboarding token not found. Please contact support.');
                }
            } else if (result.user.is_admin) {
                // Redirect to setup wizard if owner hasn't completed it
                if (result.user.is_company_creator && !result.user.setup_completed) {
                    navigate('/admin/setup');
                } else {
                    navigate('/admin');
                }
            } else {
                // Non-admin employee
                navigate('/employee');
            }
        } else {
            setError(result.error);
        }
    };

    return (
        <div className="landing-body" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff', padding: '40px 20px' }}>
            <style>
                {`
                    .form-group {
                        position: relative;
                        margin-bottom: 20px;
                    }
                    .form-label {
                        display: block;
                        font-size: 0.8rem;
                        font-weight: 700;
                        color: var(--lp-secondary-navy);
                        margin-bottom: 6px;
                        text-transform: uppercase;
                        letter-spacing: 0.05em;
                    }
                    .form-input {
                        width: 100%;
                        padding: 12px 16px 12px 42px;
                        border: 1px solid var(--lp-border);
                        border-radius: 12px;
                        font-family: 'Outfit', sans-serif;
                        font-size: 0.95rem;
                        background: #f8fafc;
                        transition: all 0.3s ease;
                        box-sizing: border-box;
                        color: var(--lp-secondary-navy);
                    }
                    .form-input:focus {
                        outline: none;
                        border-color: var(--lp-primary-orange);
                        background: white;
                        box-shadow: 0 0 0 4px rgba(232, 111, 74, 0.05);
                    }
                    .form-icon {
                        position: absolute;
                        left: 14px;
                        top: 36px;
                        color: #94a3b8;
                    }
                    .form-icon-right {
                        position: absolute;
                        right: 14px;
                        top: 36px;
                        color: #94a3b8;
                        cursor: pointer;
                        background: none;
                        border: none;
                        padding: 0;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                    }
                    .form-icon-right:hover {
                        color: var(--lp-secondary-navy);
                    }
                    .login-card {
                        background: white;
                        padding: 40px;
                        border-radius: 24px;
                        border: 1px solid var(--lp-border);
                        box-shadow: 0 20px 40px rgba(0,0,0,0.04);
                        max-width: 440px;
                        width: 100%;
                        animation: loginFadeIn 0.6s ease-out forwards;
                    }
                    @keyframes loginFadeIn {
                        from { opacity: 0; transform: translateY(20px); }
                        to { opacity: 1; transform: translateY(0); }
                    }
                `}
            </style>

            <div className="login-card">
                {/* Back Button */}
                <button
                    onClick={() => navigate('/')}
                    style={{ background: 'none', border: 'none', display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--lp-text-muted)', cursor: 'pointer', marginBottom: '24px', padding: 0, fontSize: '0.95rem', fontWeight: 500 }}
                    onMouseOver={(e) => e.currentTarget.style.color = 'var(--lp-primary-orange)'}
                    onMouseOut={(e) => e.currentTarget.style.color = 'var(--lp-text-muted)'}
                >
                    <ArrowLeft size={18} />
                    <span>Back to Home</span>
                </button>

                {/* Header */}
                <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '16px' }}>
                        <i className="fa-solid fa-layer-group" style={{ color: 'var(--lp-primary-orange)', fontSize: '2rem' }}></i>
                        <span style={{ color: 'var(--lp-primary-orange)', fontSize: '2rem', fontWeight: 800, letterSpacing: '-0.02em' }}>envion</span>
                    </div>
                    <h1 style={{ fontSize: '2rem', fontWeight: 800, color: 'var(--lp-secondary-navy)', marginBottom: '8px' }}>Welcome Back</h1>
                    <p style={{ color: 'var(--lp-text-muted)', fontSize: '1rem', margin: 0 }}>Sign in to your account to continue</p>
                </div>

                {/* Error Message */}
                {error && (
                    <div style={{ marginBottom: '24px', padding: '12px 16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '12px', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                        <XCircle size={18} style={{ color: '#ef4444', flexShrink: 0, marginTop: '2px' }} />
                        <p style={{ color: '#b91c1c', fontSize: '0.9rem', margin: 0 }}>{error}</p>
                    </div>
                )}

                {/* Form */}
                <form onSubmit={handleSubmit}>
                    {/* Email */}
                    <div className="form-group">
                        <label className="form-label">Email Address</label>
                        <Mail size={18} className="form-icon" />
                        <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            className="form-input"
                            placeholder="admin@company.com"
                            required
                        />
                    </div>

                    {/* Password */}
                    <div className="form-group">
                        <label className="form-label">Password</label>
                        <Lock size={18} className="form-icon" />
                        <input
                            type={showPassword ? 'text' : 'password'}
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            className="form-input"
                            style={{ paddingRight: '42px' }}
                            placeholder="••••••••"
                            required
                        />
                        <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="form-icon-right"
                        >
                            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                    </div>

                    {/* Remember Me & Forgot Password */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                            <input
                                type="checkbox"
                                style={{ width: '16px', height: '16px', accentColor: 'var(--lp-primary-orange)', cursor: 'pointer' }}
                            />
                            <span style={{ fontSize: '0.85rem', color: 'var(--lp-text-muted)', fontWeight: 500 }}>Remember me</span>
                        </label>
                        <button
                            type="button"
                            style={{ background: 'none', border: 'none', padding: 0, fontSize: '0.85rem', fontWeight: 700, color: 'var(--lp-primary-orange)', cursor: 'pointer' }}
                            onMouseOver={(e) => e.currentTarget.style.color = '#d05d3a'}
                            onMouseOut={(e) => e.currentTarget.style.color = 'var(--lp-primary-orange)'}
                        >
                            Forgot password?
                        </button>
                    </div>

                    {/* Submit Button */}
                    <button
                        type="submit"
                        disabled={loading}
                        style={{
                            width: '100%', padding: '14px', background: 'var(--lp-primary-orange)', color: 'white',
                            border: 'none', borderRadius: '12px', fontWeight: 800, fontSize: '1.1rem', cursor: loading ? 'not-allowed' : 'pointer',
                            transition: 'all 0.3s ease', boxShadow: '0 10px 20px rgba(232, 111, 74, 0.2)', opacity: loading ? 0.8 : 1
                        }}
                        onMouseOver={(e) => !loading && (e.currentTarget.style.transform = 'translateY(-2px)', e.currentTarget.style.boxShadow = '0 15px 30px rgba(232, 111, 74, 0.3)')}
                        onMouseOut={(e) => !loading && (e.currentTarget.style.transform = 'translateY(0)', e.currentTarget.style.boxShadow = '0 10px 20px rgba(232, 111, 74, 0.2)')}
                    >
                        {loading ? <><i className="fa-solid fa-circle-notch fa-spin" style={{ marginRight: '8px' }}></i>Signing In...</> : 'Sign In'}
                    </button>
                </form>

                {/* Divider */}
                {/* <div style={{ display: 'flex', alignItems: 'center', margin: '24px 0' }}>
                    <div style={{ flex: 1, height: '1px', background: 'var(--lp-border)' }}></div>
                    <span style={{ padding: '0 16px', fontSize: '0.85rem', color: '#94a3b8', fontWeight: 500 }}>Or</span>
                    <div style={{ flex: 1, height: '1px', background: 'var(--lp-border)' }}></div>
                </div> */}

                {/* Register Link */}
                {/* <p style={{ textAlign: 'center', color: 'var(--lp-text-muted)', fontSize: '0.95rem', margin: 0 }}>
                    Don't have an account?{' '}
                    <Link to="/register" style={{ color: 'var(--lp-primary-orange)', fontWeight: 700, textDecoration: 'none' }}
                        onMouseOver={(e) => e.currentTarget.style.color = '#d05d3a'}
                        onMouseOut={(e) => e.currentTarget.style.color = 'var(--lp-primary-orange)'}
                    >
                        Create Account
                    </Link>
                </p> */}
            </div>
        </div>
    );
};

export default Login;

